import classnames from 'classnames';
import React, { useEffect, useRef, FC } from 'react';
import { Route, Switch, matchPath, Redirect, useLocation, useHistory } from 'react-router-dom';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { TalentActivity } from '@src/views/TalentActivity';
import { Settings } from '@src/views/Settings';
import { Inbox } from '@src/views/Inbox';
import { Company } from '@src/views/Company';
import { Openings } from '@src/views/Openings';
import { FunnelOverview } from '@src/views/FunnelOverview';
import FunnelPreview from '@src/views/FunnelPreview';
import { CreateFunnel } from '@src/views/CreateFunnel';
import { Register } from '@src/views/Register';
import { Login } from '@src/views/Login';
import { ForgetPassword } from '@src/views/ForgetPassword';
import { ResetPassword } from '@src/views/ResetPassword';
import { UserProfile } from '@src/views/UserProfile';
import { PageNotFound } from '@src/views/PageNotFound';
import { PipelineTalents } from '@src/views/PipelineTalents';
import FlashMessagesContainer from '@src/containers/FlashMessagesContainer';
import { NotificationCenter } from '@src/containers/NotificationCenter';
import { Hours24Banner } from '@src/containers/Hours24Banner';
import { FeaturedApplicants } from '@src/views/FeaturedApplicants';
import { useAppSelector } from '@src/redux';
import { BodyBackdrop } from '@src/components/BodyBackdrop';
import { getAccessToken } from '@src/redux/selectors/accessToken';
import { LocalStorageKeys } from '@src/enums/localStorageKeys.enum';
import { PublishedOpeningApplicants } from '@src/views/PublishedOpeningApplicants';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { Opening } from '@src/views/Opening';
import { getRedirectUrl } from '@src/util/url';
import { createLink } from '@src/util/createLink';
import { useCompanyLazyQuery, useIpDetailsQuery } from '@src/graphql/generated';
import { PublicOpenings } from '@src/views/PublicOpenings';
import { trackEvent } from '@src/metrics';
import { Subscriptions } from './Subscriptions';
import Modal from './Modal';
import Notice from './Notice';
import SimpleHeader from './SimpleHeader';
import { LeftMenu } from './LeftMenu';
import { Session } from './Session';
import {
  TALENT_ACTIVITY_ROUTE,
  SETTINGS_ROUTE,
  FUNNEL_ROUTE,
  FUNNEL_PREVIEW_ROUTE,
  FUNNEL_OVERVIEW_ROUTE,
  FUNNEL_MAIN,
  CHATS_ROUTE,
  COMPANY_PROFILE_ROUTE,
  COMPANY_PROFILE_OLD_ROUTE,
  SOURCE_ROUTE,
  REGISTER_ROUTE,
  LOGIN_ROUTE,
  INDEX_ROUTE,
  FORGET_PASSWORD_ROUTE,
  RESET_PASSWORD_ROUTE,
  USER_PROFILE_ROUTE,
  PIPELINE_TALENTS_ROUTE,
  FEATURED_APPLICANTS_ROUTE,
  OPENINGS_ROUTE,
  PUBLISHED_OPENING_APPLICANTS,
  OLD_CHATS_ROUTE,
  AGENCY_ROUTE,
  PUBLIC_OPENINGS_ROUTE,
} from './links';
import { QueryParamsInterface } from './interfaces/queryParams.interface';
import './styles.scss';

export const Routes: FC = () => {
  const isMounted = useRef<boolean>(false);

  const history = useHistory();

  const accessTokenInRedux = useAppSelector(getAccessToken);

  const location = useLocation();

  const { queryParams } = useQueryParams<QueryParamsInterface>();

  const { data: ipDetailsQueryResult } = useIpDetailsQuery();

  const [fetchCompany, { data }] = useCompanyLazyQuery();

  const isPreviewPage = !!matchPath(location.pathname, { path: FUNNEL_PREVIEW_ROUTE });
  const oldChatsRouteMatch = matchPath<{ threadId: string }>(location.pathname, { path: OLD_CHATS_ROUTE });

  const accessTokenInLocalStorage = localStorage.getItem(LocalStorageKeys.TOKEN_STORAGE_KEY);

  const isLoggedIn = accessTokenInRedux || accessTokenInLocalStorage;

  const company = data?.company;

  useEffect(() => {
    if (isLoggedIn && !isMounted.current) {
      isMounted.current = true;
      trackEvent({
        context: MetricContext.SESSION,
        interaction: MetricAction.USER_LOADED,
      });
    }

    if (isLoggedIn && !company) {
      fetchCompany();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && queryParams.redirectUrl) {
      const url = new URL(queryParams.redirectUrl, document.location.origin);
      url.searchParams.delete('redirectUrl');
      url.searchParams.delete('loginToken');

      history.replace({ pathname: url.pathname, search: url.search });
    }
  }, [isLoggedIn, queryParams]);

  if (!ipDetailsQueryResult?.ipDetails) {
    return null;
  }

  return (
    <>
      {!isLoggedIn && (
        <>
          <Switch location={location}>
            <Route exact path={INDEX_ROUTE}>
              <Redirect
                to={createLink({
                  link: LOGIN_ROUTE,
                  queryParams: {
                    loginToken: queryParams.loginToken,
                    redirectUrl: queryParams.loginToken ? getRedirectUrl(location) : undefined,
                  },
                })}
              />
            </Route>

            <Route path={LOGIN_ROUTE}>
              <Login />
            </Route>

            <Route exact path={REGISTER_ROUTE}>
              <Register />
            </Route>

            <Route exact path={FORGET_PASSWORD_ROUTE}>
              <ForgetPassword />
            </Route>

            <Route exact path={RESET_PASSWORD_ROUTE}>
              <ResetPassword />
            </Route>

            <Route path="*">
              <Redirect
                to={createLink({
                  link: LOGIN_ROUTE,
                  queryParams: {
                    loginToken: queryParams.loginToken,
                    redirectUrl: queryParams.loginToken ? getRedirectUrl(location) : undefined,
                  },
                })}
              />
            </Route>
          </Switch>
        </>
      )}

      {isLoggedIn && (
        <Session isProfileFinishModalDisabled={isPreviewPage}>
          <Subscriptions>
            <div className={classnames('page-wrapper', isPreviewPage && 'flex-column')}>
              {isPreviewPage ? <SimpleHeader /> : <LeftMenu />}

              <div className="page-content-wrapper">
                {!isPreviewPage && <Hours24Banner />}
                {!isPreviewPage && <Notice />}

                <Switch location={location}>
                  <Route exact path={INDEX_ROUTE}>
                    <Redirect to={FEATURED_APPLICANTS_ROUTE} />
                  </Route>

                  <Route exact path={TALENT_ACTIVITY_ROUTE}>
                    <TalentActivity />
                  </Route>

                  <Route exact path={OLD_CHATS_ROUTE}>
                    <Redirect
                      to={createLink({
                        link: CHATS_ROUTE,
                        queryParams: {
                          threadId: oldChatsRouteMatch?.params.threadId,
                        },
                      })}
                    />
                  </Route>

                  <Route exact path={CHATS_ROUTE}>
                    <Inbox />
                  </Route>

                  <Route exact path={FUNNEL_ROUTE}>
                    <Opening />
                  </Route>

                  <Route exact path={FUNNEL_PREVIEW_ROUTE}>
                    <FunnelPreview />
                  </Route>

                  <Route path={FUNNEL_MAIN}>
                    <CreateFunnel />
                  </Route>

                  <Route path={FUNNEL_OVERVIEW_ROUTE}>
                    <FunnelOverview />
                  </Route>

                  <Route path={PUBLISHED_OPENING_APPLICANTS}>
                    <PublishedOpeningApplicants />
                  </Route>

                  <Route path={COMPANY_PROFILE_ROUTE}>
                    <Company />
                  </Route>

                  <Route path={SOURCE_ROUTE}>
                    {location.search.length && !queryParams.activeRecruiter ? (
                      <Redirect
                        to={createLink({
                          link: PIPELINE_TALENTS_ROUTE,
                          queryParams: queryParams,
                        })}
                      />
                    ) : (
                      <Redirect to={FEATURED_APPLICANTS_ROUTE} />
                    )}
                  </Route>

                  <Route path={PIPELINE_TALENTS_ROUTE}>
                    <PipelineTalents />
                  </Route>

                  <Route path={SETTINGS_ROUTE}>
                    <Settings />
                  </Route>

                  <Route exact path={COMPANY_PROFILE_OLD_ROUTE}>
                    <Redirect
                      to={createLink({
                        link: COMPANY_PROFILE_ROUTE,
                        params: {
                          companyProfileId: company?.profile?.id,
                        },
                      })}
                    />
                  </Route>

                  <Route path={USER_PROFILE_ROUTE}>
                    <UserProfile />
                  </Route>

                  <Route exact path={FEATURED_APPLICANTS_ROUTE}>
                    <FeaturedApplicants />
                  </Route>

                  <Route exact path={PUBLIC_OPENINGS_ROUTE}>
                    <PublicOpenings />
                  </Route>

                  <Route exact path={OPENINGS_ROUTE}>
                    <Openings />
                  </Route>

                  <Route path={AGENCY_ROUTE}>
                    <Redirect to={OPENINGS_ROUTE} />
                  </Route>

                  <Route path={LOGIN_ROUTE}>
                    <Redirect to={INDEX_ROUTE} />
                  </Route>

                  <Route component={PageNotFound} path="*" />
                </Switch>
              </div>

              <Modal />

              <BodyBackdrop />

              <NotificationCenter />
            </div>
          </Subscriptions>
        </Session>
      )}

      <FlashMessagesContainer />
    </>
  );
};
